import { useEffect, useRef, useState } from "react";
import { transferClubAdmin } from "../../services/api";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../store/slices/authSlice";
import ConfirmationModal from "./ConfirmationModal";
import CoachInviteModal from "./CoachInviteModal";
import { labelForStaffRole } from "../../constants/staffRoles";
// import ChangeRoleModal from "./ChangeRoleModal";

const StaffCard = ({ staff }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [isRemovingModalOpen, setIsRemovingModalOpen] = useState(false);
  const [isStaffRemoving, setIsStaffRemoving] = useState(false);
  const [isChangeRoleOpen, setIsChangeRoleOpen] = useState(false);
  const [isRoleSaving, setIsRoleSaving] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);
  // Capitalize-first safety net so any path returns "President" not "president".
  const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  // Helper function to get position display text
  const getPositionDisplay = (staff) => {
    if (staff.role_title_custom) {
      return capFirst(staff.role_title_custom);
    }

    if (staff.role_type) {
      // labelForStaffRole knows every value from STAFF_ROLE_OPTIONS and falls
      // back to a snake_case → "Snake Case" pretty form for unknowns.
      return capFirst(labelForStaffRole(staff.role_type));
    }

    // T039: /users/club/my/staff/ puts the raw role_type enum into `position`
    // (users/views.py MyClubStaffListView), so this fallback — not the
    // role_type branch above — is the live path for staff rows and used to
    // render "Assistant_coach". Route it through the same catalog.
    return capFirst(labelForStaffRole(staff.position)) || "Staff Member";
  };

  // Get profile image - prioritize logo from API
  const profileImage = staff.logo || staff.profile_picture_url || staff.image;

  const handleMakeAdmin = async () => {
    console.log("handleMakeAdmin", staff);
    const targetAccountId = staff?.account_id;
    if (!targetAccountId) {
      toast.error("Unable to identify staff to promote.");
      return;
    }
    try {
      setIsTransferring(true);
      const res = await transferClubAdmin(targetAccountId);
      if (!res.success) {
        toast.error(res.error || "Failed to transfer admin");
        return;
      }
      toast.success(
        `${
          res.message || "Club admin transferred successfully"
        }. Logging out in 3 seconds...`
      );
      setTimeout(() => {
        dispatch(logout());
        navigate("/login");
      }, 3000);
    } catch (e) {
      toast.error("Failed to transfer admin");
    } finally {
      setIsTransferring(false);
      setIsMenuOpen(false);
    }
  };

  const handleOpenRemoveModal = () => {
    if (isTransferring) {
      return;
    }
    setIsMenuOpen(false);
    setIsRemovingModalOpen(true);
  };

  const handleOpenChangeRoleModal = () => {
    if (isTransferring) {
      return;
    }
    setIsMenuOpen(false);
    setIsChangeRoleOpen(true);
  };

  const handleConfirmRemove = async () => {
    try {
      setIsStaffRemoving(true);
      console.log("Mock remove staff from club", {
        staffId: staff?.account_id || staff?.id,
        name: staff?.name,
      });
    } finally {
      setIsStaffRemoving(false);
      setIsRemovingModalOpen(false);
    }
  };

  const handleSaveRoleChange = async (position) => {
    if (!position) {
      return;
    }
    try {
      setIsRoleSaving(true);
      setIsMenuOpen(false);
      console.log("Mock change staff role", {
        staffId: staff?.account_id || staff?.id,
        name: staff?.name,
        roleType: position.roleType,
        staffType: position.staffType,
        positionValue: position.value,
        label: position.name,
      });
    } finally {
      setIsRoleSaving(false);
      setIsChangeRoleOpen(false);
    }
  };

  const handleInviteToTransferPitch = () => {
    setIsInviteModalOpen(true);
  };

  // Get coach_id from staff data (could be coach_id, ts_coach_id, thesports_coach_id, etc.)
  const getCoachId = () => {
    return staff.coach_id || staff.ts_coach_id || staff.thesports_coach_id || staff.id;
  };

  // Handle card click navigation
  const handleCardClick = () => {
    // Navigate based on available IDs
    if (staff.ts_coach_id) {
      navigate(`/search/coach/${staff.ts_coach_id}`);
    } else if (staff.account_id) {
      navigate(`/search/staff/${staff.account_id}`);
    }
  };

  return (
    <div 
      className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 relative cursor-pointer hover:shadow-md transition-shadow"
      
      role="button"
      tabIndex="0"
      aria-label={`View ${staff.name || 'staff member'} profile`}
    >
      {/* Header Section with Profile Picture, Name, and Menu */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3" onClick={handleCardClick}>
          {profileImage ? (
            <img
              src={profileImage}
              alt={staff.name}
              className="w-12 h-12 rounded-xl object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center text-sm font-bold text-gray-600"
            style={{ display: profileImage ? "none" : "flex" }}
          >
            {staff.name?.charAt(0) || "S"}
          </div>
          <h3 className="font-bold text-lg text-gray-900">{staff.name}</h3>
        </div>
        {/* <button
          className="text-gray-400 hover:text-gray-600 p-1"
          aria-label="Open staff actions"
          tabIndex="0"
          onClick={() => setIsMenuOpen((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setIsMenuOpen((v) => !v);
          }}
        >
          <svg
            width="12"
            height="4"
            viewBox="0 0 12 4"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.00009 2.63601C6.35154 2.63601 6.63645 2.3511 6.63645 1.99964C6.63645 1.64819 6.35154 1.36328 6.00009 1.36328C5.64864 1.36328 5.36373 1.64819 5.36373 1.99964C5.36373 2.3511 5.64864 2.63601 6.00009 2.63601Z"
              stroke="#2C2C2C"
              stroke-width="1.27273"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M10.4546 2.63601C10.8061 2.63601 11.091 2.3511 11.091 1.99964C11.091 1.64819 10.8061 1.36328 10.4546 1.36328C10.1032 1.36328 9.81827 1.64819 9.81827 1.99964C9.81827 2.3511 10.1032 2.63601 10.4546 2.63601Z"
              stroke="#2C2C2C"
              stroke-width="1.27273"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M1.54554 2.63601C1.897 2.63601 2.18191 2.3511 2.18191 1.99964C2.18191 1.64819 1.897 1.36328 1.54554 1.36328C1.19409 1.36328 0.90918 1.64819 0.90918 1.99964C0.90918 2.3511 1.19409 2.63601 1.54554 2.63601Z"
              stroke="#2C2C2C"
              stroke-width="1.27273"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button> */}
        {isMenuOpen && (
          <div
            ref={popoverRef}
            className="absolute right-2 top-12 z-10 border border-[#D6DBEA] bg-white p-3 rounded-lg shadow-lg"
          >
            <button
              onClick={handleMakeAdmin}
              disabled={isTransferring}
              className={`block w-full text-left text-[#0A0F2D] text-sm hover:bg-gray-50 px-2 py-1 rounded ${
                isTransferring ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isTransferring ? "Transferring…" : "Make Admin"}
            </button>

            <button
              onClick={handleOpenRemoveModal}
              disabled={isTransferring}
              className={`block w-full text-left text-[#0A0F2D] text-sm hover:bg-gray-50 px-2 py-1 rounded ${
                isTransferring ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isStaffRemoving ? "Removing..." : "Remove From Club"}
            </button>

            <button
              onClick={handleOpenChangeRoleModal}
              disabled={isTransferring}
              className={`block w-full text-left text-[#0A0F2D] text-sm hover:bg-gray-50 px-2 py-1 rounded ${
                isTransferring ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isRoleSaving ? "Saving..." : "Change Role"}
            </button>
          </div>
        )}
      </div>

      {/* Position Section */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-[#242426B2]">Position</span>
        <span className="text-xs  text-[#242426]">
          {getPositionDisplay(staff)}
        </span>
      </div>

      {/* Contact Information Section */}
      <div className="bg-[#F7F7F7] rounded-lg p-3 space-y-2">
        {staff.is_platform_user === false ? (
          <div className="flex items-center gap-2">
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 2H10V10H2V2Z"
                stroke="#1E1E1E"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 6H10M6 2V10"
                stroke="#1E1E1E"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-xs text-[#222A35]">
              {staff.preferred_formation || "N/A"}
            </span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.52472 2.5C8.01309 2.59528 8.46192 2.83413 8.81375 3.18597C9.16559 3.53781 9.40444 3.98663 9.49972 4.475M7.52472 0.5C8.53936 0.612718 9.48552 1.06709 10.2078 1.7885C10.9302 2.50992 11.3857 3.4555 11.4997 4.47M10.9997 8.46V9.96C11.0003 10.0993 10.9718 10.2371 10.916 10.3647C10.8602 10.4923 10.7784 10.6068 10.6758 10.7009C10.5732 10.7951 10.452 10.8667 10.3201 10.9114C10.1882 10.956 10.0484 10.9725 9.90972 10.96C8.37114 10.7928 6.89322 10.2671 5.59472 9.425C4.38664 8.65733 3.36239 7.63309 2.59472 6.425C1.74971 5.1206 1.22384 3.6355 1.05972 2.09C1.04723 1.95173 1.06366 1.81238 1.10797 1.68081C1.15229 1.54924 1.22351 1.42834 1.31711 1.32581C1.4107 1.22328 1.52462 1.14135 1.65162 1.08526C1.77861 1.02917 1.91589 1.00013 2.05472 1H3.55472C3.79738 0.997612 4.03262 1.08354 4.2166 1.24177C4.40059 1.39999 4.52076 1.61972 4.55472 1.86C4.61803 2.34003 4.73545 2.81136 4.90472 3.265C4.97199 3.44396 4.98655 3.63846 4.94668 3.82544C4.9068 4.01242 4.81415 4.18406 4.67972 4.32L4.04472 4.955C4.7565 6.20677 5.79295 7.24322 7.04472 7.955L7.67972 7.32C7.81567 7.18557 7.9873 7.09292 8.17428 7.05305C8.36126 7.01317 8.55576 7.02773 8.73472 7.095C9.18836 7.26428 9.65969 7.38169 10.1397 7.445C10.3826 7.47926 10.6044 7.6016 10.763 7.78875C10.9216 7.97589 11.0058 8.21479 10.9997 8.46Z"
                  stroke="#1E1E1E"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-xs text-[#222A35]">
                {staff.phone_number || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* shrink-0 keeps the envelope at its 12px: once break-all lets
                  the address shrink, the two items share the shortfall and the
                  icon was the one giving way (down to 6.3px at 768). */}
              <svg
                className="shrink-0"
                width="12"
                height="10"
                viewBox="0 0 12 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 2C11 1.45 10.55 1 10 1H2C1.45 1 1 1.45 1 2M11 2V8C11 8.55 10.55 9 10 9H2C1.45 9 1 8.55 1 8V2M11 2L6 5.5L1 2"
                  stroke="#1E1E1E"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {/* An address has no break opportunity, so as a flex item its
                  min-content width is the whole string and the row pushes past
                  the card. break-all lets it wrap instead, the same treatment
                  the contacts card gives the same field (ContactCard.js:181). */}
              <span className="text-xs text-[#222A35] break-all">
                {staff.email || "N/A"}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Invite Button - Only for non-platform users */}
      {staff.is_platform_user === false && (
            <button
              className="w-full mt-3 px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-900 text-sm font-medium transition-colors hover:bg-gray-50"
              onClick={(e) => {
                e.stopPropagation();
                handleInviteToTransferPitch();
              }}
            >
              Invite to TransferPitch
            </button>
      )}

      <ConfirmationModal
        isOpen={isRemovingModalOpen}
        onClose={() => {
          if (!isStaffRemoving) {
            setIsRemovingModalOpen(false);
          }
        }}
        onConfirm={handleConfirmRemove}
        variant="v2"
        message={`Are you sure you want to remove ${
          staff?.name || "this staff member"
        } from the club?`}
        confirmText={isStaffRemoving ? "Removing..." : "Remove"}
        confirmButtonClass="bg-[#FF4B4B] text-white"
        loading={isStaffRemoving}
        title="Remove staff member"
      />

      <CoachInviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={() => {
          // Optionally refresh staff list or show success message
        }}
        coachId={getCoachId()}
        coachName={staff.name}
      />

      {/* <ChangeRoleModal
        isOpen={isChangeRoleOpen}
        staff={staff}
        onClose={() => {
          if (!isRoleSaving) {
            setIsMenuOpen(false);
            setIsChangeRoleOpen(false);
          }
        }}
        onSave={handleSaveRoleChange}
        isSaving={isRoleSaving}
      /> */}
    </div>
  );
};

export default StaffCard;
